// src/pages/reval/RevalForm.jsx — Revalidación de datos (público, sin login).
//
// El colaborador de Vale Amigo teclea su número de empleado; mostramos lo que
// ya tenemos (nombre, puesto, unidad, sucursal, subdirección y zona) y puede
// marcar cualquier dato como incorrecto y escribir el correcto. Luego nos
// confirma teléfono y correo laborales y la dirección de su sucursal sede,
// con ubicación por GPS o link de Google Maps. Un registro por empleado: si
// ya existe, se actualiza (si el admin lo permite).
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || '/api' });

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
.rv-root { min-height: 100vh; background: #f3f5f9; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif; -webkit-font-smoothing: antialiased; color: #0f172a; }
.rv-root * { box-sizing: border-box; }
.rv-top { background: linear-gradient(160deg, #062a63 0%, #07357f 60%, #0a4aa8 100%); color: #fff; padding: 18px 16px 22px; position: relative; }
.rv-top::after { content: ''; position: absolute; left: 0; right: 0; bottom: 0; height: 4px; background: linear-gradient(90deg, transparent, #f3c94b 30%, #f3c94b 70%, transparent); }
.rv-top-in { max-width: 560px; margin: 0 auto; display: flex; align-items: center; gap: 12px; }
.rv-top img { height: 26px; }
.rv-top b { display: block; font-size: 15px; font-weight: 700; }
.rv-top span { font-size: 11px; letter-spacing: 1.6px; text-transform: uppercase; opacity: .75; font-weight: 600; }
.rv-main { max-width: 560px; margin: 0 auto; padding: 16px 14px 60px; }
.rv-steps { display: flex; gap: 6px; margin: 4px 0 14px; }
.rv-step { flex: 1; height: 5px; border-radius: 3px; background: #dbe2ee; }
.rv-step.on { background: #0a4aa8; }
.rv-card { background: #fff; border: 1px solid #e3e8f0; border-radius: 16px; padding: 18px 16px; box-shadow: 0 1px 2px rgba(15,23,42,.04); margin-bottom: 12px; }
.rv-h1 { font-size: 20px; font-weight: 800; margin: 0 0 6px; letter-spacing: -.01em; }
.rv-p { font-size: 14px; color: #475569; margin: 0 0 14px; line-height: 1.5; }
.rv-label { display: block; font-size: 12px; font-weight: 700; color: #334155; margin-bottom: 6px; letter-spacing: .2px; }
.rv-input, .rv-textarea { width: 100%; padding: 12px 13px; font-size: 16px; font-family: inherit; border: 1px solid #cbd5e1; border-radius: 10px; background: #fff; color: #0f172a; outline: none; transition: border-color .15s, box-shadow .15s; }
.rv-input:focus, .rv-textarea:focus { border-color: #0a4aa8; box-shadow: 0 0 0 3px rgba(10,74,168,.14); }
.rv-textarea { min-height: 84px; resize: vertical; }
.rv-input.big { font-size: 24px; letter-spacing: 2px; text-align: center; font-weight: 700; padding: 14px; }
.rv-field { margin-bottom: 14px; }
.rv-hint { font-size: 12px; color: #64748b; margin-top: 5px; line-height: 1.4; }
.rv-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; width: 100%; padding: 13px 16px; border-radius: 12px; border: 1px solid transparent; font-size: 15px; font-weight: 700; font-family: inherit; cursor: pointer; transition: all .15s; }
.rv-btn:disabled { opacity: .55; cursor: not-allowed; }
.rv-btn-primary { background: #0a4aa8; color: #fff; box-shadow: 0 1px 3px rgba(10,74,168,.3); }
.rv-btn-primary:hover:not(:disabled) { background: #07357f; }
.rv-btn-ghost { background: #fff; color: #334155; border-color: #cbd5e1; }
.rv-btn-ghost:hover:not(:disabled) { background: #f1f5f9; }
.rv-btn.sm { width: auto; padding: 8px 12px; font-size: 13px; border-radius: 9px; }
.rv-row { display: flex; gap: 10px; }
.rv-row .rv-btn { flex: 1; }
.rv-alert { border-radius: 12px; padding: 11px 14px; font-size: 13.5px; line-height: 1.5; margin-bottom: 12px; }
.rv-alert.err { background: #fee2e2; color: #991b1b; border: 1px solid #fecaca; }
.rv-alert.warn { background: #fef3c7; color: #92400e; border: 1px solid #fde68a; }
.rv-alert.info { background: #dbeafe; color: #1e3a8a; border: 1px solid #bfdbfe; }
.rv-alert.ok { background: #d1fae5; color: #065f46; border: 1px solid #a7f3d0; }

/* Datos a confirmar */
.rv-data { border: 1px solid #e3e8f0; border-radius: 12px; overflow: hidden; }
.rv-data-row { padding: 11px 14px; border-top: 1px solid #eef2f7; }
.rv-data-row:first-child { border-top: none; }
.rv-data-top { display: flex; align-items: flex-start; gap: 10px; }
.rv-data-k { font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .5px; }
.rv-data-v { font-size: 15px; font-weight: 600; color: #0f172a; margin-top: 2px; line-height: 1.3; }
.rv-data-v.empty { color: #94a3b8; font-weight: 500; font-style: italic; }
.rv-wrong { margin-left: auto; flex-shrink: 0; display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; color: #64748b; cursor: pointer; user-select: none; padding: 6px 8px; border-radius: 8px; border: 1px solid transparent; }
.rv-wrong input { width: 16px; height: 16px; accent-color: #dc2626; }
.rv-wrong.on { color: #b91c1c; background: #fef2f2; border-color: #fecaca; }
.rv-data-fix { margin-top: 10px; }
.rv-data-fix .rv-input { font-size: 15px; padding: 10px 12px; border-color: #fca5a5; background: #fff7f7; }

/* Mapa */
.rv-map { width: 100%; height: 220px; border: 0; border-radius: 12px; margin-top: 10px; background: #e2e8f0; }
.rv-geo-ok { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #065f46; font-weight: 600; margin-top: 8px; }
.rv-pill { display: inline-flex; align-items: center; gap: 6px; background: #f1f5f9; border-radius: 999px; padding: 4px 10px; font-size: 12px; font-weight: 600; color: #334155; }

/* Resumen */
.rv-sum { font-size: 14px; }
.rv-sum dt { color: #64748b; font-size: 11px; text-transform: uppercase; letter-spacing: .5px; font-weight: 700; margin-top: 10px; }
.rv-sum dd { margin: 2px 0 0; font-weight: 600; }
.rv-sum .fix { color: #b91c1c; font-weight: 500; font-size: 13px; }
.rv-done { text-align: center; padding: 10px 0; }
.rv-done .ico { width: 64px; height: 64px; border-radius: 50%; background: #d1fae5; color: #059669; display: flex; align-items: center; justify-content: center; margin: 0 auto 14px; }
.rv-folio { font-family: ui-monospace, Menlo, Consolas, monospace; font-weight: 800; font-size: 20px; letter-spacing: 2px; color: #0a4aa8; }
.rv-foot { text-align: center; font-size: 12px; color: #94a3b8; margin-top: 18px; line-height: 1.5; }
`;

const Icon = ({ d, size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>{d}</svg>
);
const I = {
    arrow: <Icon d={<><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></>} size={16} />,
    back:  <Icon d={<><path d="M19 12H5" /><path d="m12 19-7-7 7-7" /></>} size={16} />,
    pin:   <Icon d={<><path d="M12 22s7-7 7-12a7 7 0 1 0-14 0c0 5 7 12 7 12z" /><circle cx="12" cy="10" r="2.5" /></>} size={16} />,
    check: <Icon d={<path d="M20 6 9 17l-5-5" />} size={30} />,
    ok:    <Icon d={<path d="M20 6 9 17l-5-5" />} size={14} />,
};

const FIELDS = [
    ['nombre', 'Nombre'], ['puesto', 'Puesto'], ['unidad', 'Unidad de negocio'],
    ['sucursal', 'Sucursal sede'], ['subdireccion', 'Subdirección'], ['zona', 'Zona'],
];
const emptyCorr = () => Object.fromEntries(FIELDS.map(([k]) => [k, { wrong: false, correct: '' }]));
const fmtDate = (d) => d ? new Date(d).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }) : '';

export default function RevalForm() {
    const [cfg, setCfg] = useState(null);
    const [step, setStep] = useState(1);
    const [idInput, setIdInput] = useState('');
    const [card, setCard] = useState(null);
    const [err, setErr] = useState('');
    const [busy, setBusy] = useState(false);
    const [corr, setCorr] = useState(emptyCorr());
    const [confirm, setConfirm] = useState({ telefonoLaboral: '', correoLaboral: '', direccionSede: '', mapsUrl: '', lat: null, lng: null, geoAccuracy: null, geoSource: 'none' });
    const [geoBusy, setGeoBusy] = useState(false);
    const [result, setResult] = useState(null);
    const idRef = useRef(null);

    useEffect(() => {
        api.get('/reval/public/config').then(({ data }) => setCfg(data)).catch(() => setCfg({ enabled: true, title: 'Revalidación de datos', intro: '' }));
        document.title = 'Revalidación de datos · Vale Amigo';
    }, []);
    useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [step]);

    const lookup = async (e) => {
        e?.preventDefault();
        const id = idInput.replace(/\D/g, '');
        if (!id) return setErr('Escribe tu número de empleado.');
        setBusy(true); setErr('');
        try {
            const { data } = await api.get(`/reval/public/lookup/${id}`);
            setCard(data);
            setCorr(data.existing?.corrections ? { ...emptyCorr(), ...data.existing.corrections } : emptyCorr());
            if (data.existing?.confirm) setConfirm({ ...confirm, ...data.existing.confirm });
            setStep(2);
        } catch (ex) {
            setErr(ex.response?.data?.error || 'No se pudo consultar. Revisa tu conexión e intenta de nuevo.');
        } finally { setBusy(false); }
    };

    const setWrong = (k, wrong) => setCorr({ ...corr, [k]: { ...corr[k], wrong, correct: wrong ? corr[k].correct : '' } });
    const setCorrect = (k, v) => setCorr({ ...corr, [k]: { ...corr[k], correct: v } });

    const next2 = () => {
        for (const [k, label] of FIELDS) {
            if (corr[k].wrong && !corr[k].correct.trim()) return setErr(`Marcaste "${label}" como incorrecto: escribe el dato correcto.`);
        }
        setErr(''); setStep(3);
    };

    const useGps = () => {
        if (!navigator.geolocation) return setErr('Tu navegador no permite obtener la ubicación. Pega un link de Google Maps.');
        setGeoBusy(true); setErr('');
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setConfirm(c => ({ ...c, lat: +pos.coords.latitude.toFixed(6), lng: +pos.coords.longitude.toFixed(6), geoAccuracy: Math.round(pos.coords.accuracy || 0), geoSource: 'gps' }));
                setGeoBusy(false);
            },
            (e) => {
                setGeoBusy(false);
                setErr(e.code === 1 ? 'No diste permiso de ubicación. Puedes pegar un link de Google Maps en su lugar.' : 'No se pudo obtener la ubicación. Intenta de nuevo o pega un link de Google Maps.');
            },
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
        );
    };
    const onMapsUrl = (v) => {
        const m = v.match(/@(-?\d{1,2}\.\d+),(-?\d{1,3}\.\d+)/) || v.match(/[?&]q=(-?\d{1,2}\.\d+),(-?\d{1,3}\.\d+)/) || v.match(/^(-?\d{1,2}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)$/);
        setConfirm(c => ({ ...c, mapsUrl: v, ...(m ? { lat: +m[1], lng: +m[2], geoSource: 'link', geoAccuracy: null } : (c.geoSource === 'link' ? { lat: null, lng: null, geoSource: 'none' } : {})) }));
    };
    const clearGeo = () => setConfirm(c => ({ ...c, lat: null, lng: null, geoAccuracy: null, geoSource: 'none', mapsUrl: '' }));

    const next3 = () => {
        const tel = confirm.telefonoLaboral.replace(/\D/g, '');
        if (tel.length < 10) return setErr('Escribe tu teléfono laboral a 10 dígitos.');
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(confirm.correoLaboral.trim())) return setErr('Escribe un correo laboral válido.');
        if (confirm.direccionSede.trim().length < 10) return setErr('Escribe la dirección completa de tu sucursal (calle, número, colonia, municipio, estado).');
        if (cfg?.requireGeo && confirm.geoSource === 'none') return setErr('Comparte la ubicación de la sucursal con el botón "Usar mi ubicación" o un link de Google Maps.');
        setErr(''); setStep(4);
    };

    const submit = async () => {
        setBusy(true); setErr('');
        try {
            const { data } = await api.post('/reval/public/submit', { idEmpleado: card.idEmpleado, confirm, corrections: corr });
            setResult(data); setStep(5);
        } catch (ex) {
            setErr(ex.response?.data?.error || 'No se pudo enviar. Intenta de nuevo.');
        } finally { setBusy(false); }
    };

    const reset = () => { setStep(1); setIdInput(''); setCard(null); setErr(''); setCorr(emptyCorr()); setConfirm({ telefonoLaboral: '', correoLaboral: '', direccionSede: '', mapsUrl: '', lat: null, lng: null, geoAccuracy: null, geoSource: 'none' }); setResult(null); setTimeout(() => idRef.current?.focus(), 50); };

    const hasGeo = confirm.lat != null && confirm.lng != null;
    const mapSrc = hasGeo ? `https://maps.google.com/maps?q=${confirm.lat},${confirm.lng}&z=16&output=embed` : null;
    const corrCount = FIELDS.filter(([k]) => corr[k].wrong).length;

    return (
        <div className="rv-root">
            <style>{CSS}</style>
            <header className="rv-top">
                <div className="rv-top-in">
                    <img src="/caprepa.svg" alt="Caprepa" />
                    <div><b>{cfg?.title || 'Revalidación de datos'}</b><span>Vale Amigo</span></div>
                </div>
            </header>
            <main className="rv-main">
                {step < 5 && (
                    <div className="rv-steps">{[1, 2, 3, 4].map(n => <div key={n} className={`rv-step ${step >= n ? 'on' : ''}`} />)}</div>
                )}
                {cfg && !cfg.enabled && <div className="rv-alert warn">El registro está cerrado por el momento.</div>}
                {err && <div className="rv-alert err">{err}</div>}

                {/* ── Paso 1: número de empleado ─────────────────────────────── */}
                {step === 1 && (
                    <form className="rv-card" onSubmit={lookup}>
                        <h1 className="rv-h1">Hola 👋</h1>
                        <p className="rv-p">{cfg?.intro || 'Ayúdanos a confirmar tus datos de contacto y la dirección de tu sucursal.'}</p>
                        <div className="rv-field">
                            <label className="rv-label" htmlFor="rv-id">Tu número de empleado</label>
                            <input id="rv-id" ref={idRef} className="rv-input big" inputMode="numeric" pattern="[0-9]*" autoComplete="off" autoFocus
                                   placeholder="00000" value={idInput} onChange={e => setIdInput(e.target.value.replace(/\D/g, '').slice(0, 9))} disabled={cfg && !cfg.enabled} />
                            <div className="rv-hint">Es el que viene en tu credencial o recibo de nómina.</div>
                        </div>
                        <button type="submit" className="rv-btn rv-btn-primary" disabled={busy || !idInput || (cfg && !cfg.enabled)}>{busy ? 'Buscando…' : <>Continuar {I.arrow}</>}</button>
                        {cfg?.contactText && <p className="rv-foot">{cfg.contactText}</p>}
                    </form>
                )}

                {/* ── Paso 2: confirmar datos / marcar incorrectos ──────────── */}
                {step === 2 && card && (
                    <>
                        {card.existing && (
                            <div className={`rv-alert ${card.existing.canUpdate ? 'info' : 'warn'}`}>
                                Ya tienes un registro del {fmtDate(card.existing.lastSubmittedAt)}.
                                {card.existing.canUpdate ? ' Si continúas, se actualiza con lo que captures ahora.' : ' Ya no se puede modificar; si necesitas corregir algo, contacta a tu gerente.'}
                            </div>
                        )}
                        <div className="rv-card">
                            <h1 className="rv-h1">¿Estos datos son correctos?</h1>
                            <p className="rv-p">Es lo que tenemos registrado. Si algo no cuadra, marca <b>"Está mal"</b> y escribe el dato correcto.</p>
                            <div className="rv-data">
                                {FIELDS.map(([k, label]) => {
                                    const v = card[k];
                                    const on = corr[k].wrong;
                                    return (
                                        <div key={k} className="rv-data-row">
                                            <div className="rv-data-top">
                                                <div style={{ minWidth: 0 }}>
                                                    <div className="rv-data-k">{label}</div>
                                                    <div className={`rv-data-v ${v ? '' : 'empty'}`}>{v || (k === 'subdireccion' || k === 'zona' ? 'Sin dato (indícala si la conoces)' : 'Sin dato')}</div>
                                                </div>
                                                <label className={`rv-wrong ${on ? 'on' : ''}`}>
                                                    <input type="checkbox" checked={on} onChange={e => setWrong(k, e.target.checked)} />
                                                    {on ? 'Está mal' : v ? 'Está mal' : 'Indicar'}
                                                </label>
                                            </div>
                                            {on && (
                                                <div className="rv-data-fix">
                                                    <input className="rv-input" placeholder={`${label} correcto/a`} value={corr[k].correct} onChange={e => setCorrect(k, e.target.value)} maxLength={200} autoFocus />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            {!card.catalogMatched && (card.subdireccion === '' && card.zona === '') && (
                                <div className="rv-hint" style={{ marginTop: 10 }}>Aún no tenemos tu sucursal en el catálogo de subdirecciones; si conoces tu subdirección y zona, indícalas con "Indicar".</div>
                            )}
                        </div>
                        <div className="rv-row">
                            <button type="button" className="rv-btn rv-btn-ghost" onClick={() => { setStep(1); setErr(''); }}>{I.back} Atrás</button>
                            <button type="button" className="rv-btn rv-btn-primary" onClick={next2} disabled={card.existing && !card.existing.canUpdate}>
                                {corrCount ? `Continuar (${corrCount} por corregir)` : 'Sí, son correctos'} {I.arrow}
                            </button>
                        </div>
                    </>
                )}

                {/* ── Paso 3: contacto + dirección + ubicación ─────────────── */}
                {step === 3 && card && (
                    <>
                        <div className="rv-card">
                            <h1 className="rv-h1">Tus datos de contacto</h1>
                            <p className="rv-p">Estos no los tenemos. Son los que usaremos para comunicarnos contigo por temas de trabajo.</p>
                            <div className="rv-field">
                                <label className="rv-label" htmlFor="rv-tel">Teléfono laboral</label>
                                <input id="rv-tel" className="rv-input" type="tel" inputMode="tel" autoComplete="tel" placeholder="10 dígitos" value={confirm.telefonoLaboral}
                                       onChange={e => setConfirm({ ...confirm, telefonoLaboral: e.target.value.replace(/[^\d+\s-]/g, '').slice(0, 18) })} />
                                <div className="rv-hint">El celular o número donde atiendes asuntos de la sucursal.</div>
                            </div>
                            <div className="rv-field">
                                <label className="rv-label" htmlFor="rv-mail">Correo laboral</label>
                                <input id="rv-mail" className="rv-input" type="email" inputMode="email" autoComplete="email" autoCapitalize="off" placeholder="nombre@valeamigo.com" value={confirm.correoLaboral}
                                       onChange={e => setConfirm({ ...confirm, correoLaboral: e.target.value.trim() })} />
                            </div>
                        </div>
                        <div className="rv-card">
                            <h1 className="rv-h1" style={{ fontSize: 18 }}>Dirección de tu sucursal sede</h1>
                            <p className="rv-p">{card.sucursal ? <>Sucursal <b>{corr.sucursal.wrong ? corr.sucursal.correct : card.sucursal}</b>.</> : null} Escríbela completa como la darías a un repartidor.</p>
                            <div className="rv-field">
                                <textarea className="rv-textarea" placeholder="Calle y número, colonia, código postal, municipio, estado. Referencias si ayudan." value={confirm.direccionSede}
                                          onChange={e => setConfirm({ ...confirm, direccionSede: e.target.value.slice(0, 500) })} />
                            </div>
                            <label className="rv-label">Ubicación en el mapa {cfg?.requireGeo ? '' : <span style={{ color: '#94a3b8', fontWeight: 500 }}>(opcional, pero nos ayuda mucho)</span>}</label>
                            <div className="rv-row" style={{ marginBottom: 10 }}>
                                <button type="button" className="rv-btn rv-btn-ghost" onClick={useGps} disabled={geoBusy}>{I.pin} {geoBusy ? 'Obteniendo…' : 'Usar mi ubicación'}</button>
                            </div>
                            <div className="rv-field">
                                <input className="rv-input" style={{ fontSize: 14 }} placeholder="…o pega aquí un link de Google Maps de la sucursal" value={confirm.mapsUrl} onChange={e => onMapsUrl(e.target.value)} />
                                <div className="rv-hint">En Google Maps busca la sucursal, toca "Compartir" y pega el link. Si estás en la sucursal, "Usar mi ubicación" es lo más rápido.</div>
                            </div>
                            {hasGeo && (
                                <>
                                    <div className="rv-geo-ok">{I.ok} Ubicación capturada {confirm.geoSource === 'gps' && confirm.geoAccuracy ? `(precisión ±${confirm.geoAccuracy} m)` : ''}
                                        <button type="button" className="rv-btn rv-btn-ghost sm" style={{ marginLeft: 'auto' }} onClick={clearGeo}>Quitar</button>
                                    </div>
                                    <iframe title="Mapa de la sucursal" className="rv-map" src={mapSrc} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                                    <div className="rv-hint">Verifica que el pin esté en tu sucursal. Si no, vuelve a tomar la ubicación desde dentro de la sucursal o pega el link correcto.</div>
                                </>
                            )}
                        </div>
                        <div className="rv-row">
                            <button type="button" className="rv-btn rv-btn-ghost" onClick={() => { setStep(2); setErr(''); }}>{I.back} Atrás</button>
                            <button type="button" className="rv-btn rv-btn-primary" onClick={next3}>Revisar {I.arrow}</button>
                        </div>
                    </>
                )}

                {/* ── Paso 4: resumen y envío ───────────────────────────────── */}
                {step === 4 && card && (
                    <>
                        <div className="rv-card">
                            <h1 className="rv-h1">Revisa antes de enviar</h1>
                            <dl className="rv-sum" style={{ margin: 0 }}>
                                <dt>Número de empleado</dt><dd>{card.idEmpleado}</dd>
                                {FIELDS.map(([k, label]) => (
                                    <React.Fragment key={k}>
                                        <dt>{label}</dt>
                                        <dd>{corr[k].wrong ? <><s style={{ color: '#94a3b8', fontWeight: 400 }}>{card[k] || '—'}</s> → {corr[k].correct} <span className="fix">(corrección)</span></> : (card[k] || <span style={{ color: '#94a3b8' }}>—</span>)}</dd>
                                    </React.Fragment>
                                ))}
                                <dt>Teléfono laboral</dt><dd>{confirm.telefonoLaboral}</dd>
                                <dt>Correo laboral</dt><dd>{confirm.correoLaboral}</dd>
                                <dt>Dirección de la sucursal</dt><dd style={{ whiteSpace: 'pre-wrap' }}>{confirm.direccionSede}</dd>
                                <dt>Ubicación</dt><dd>{hasGeo ? <span className="rv-pill">{I.pin} {confirm.lat}, {confirm.lng}</span> : <span style={{ color: '#94a3b8' }}>Sin ubicación</span>}</dd>
                            </dl>
                        </div>
                        <div className="rv-row">
                            <button type="button" className="rv-btn rv-btn-ghost" onClick={() => { setStep(3); setErr(''); }} disabled={busy}>{I.back} Corregir</button>
                            <button type="button" className="rv-btn rv-btn-primary" onClick={submit} disabled={busy}>{busy ? 'Enviando…' : <>Enviar {I.arrow}</>}</button>
                        </div>
                    </>
                )}

                {/* ── Paso 5: listo ─────────────────────────────────────────── */}
                {step === 5 && result && (
                    <div className="rv-card rv-done">
                        <div className="ico">{I.check}</div>
                        <h1 className="rv-h1">{result.updated ? '¡Datos actualizados!' : '¡Gracias, quedó registrado!'}</h1>
                        <p className="rv-p">Folio <span className="rv-folio">{result.folio}</span></p>
                        <p className="rv-p">{card?.nombre}, tus datos ya están con el equipo. {cfg?.allowUpdates ? 'Si algo cambia, puedes volver a entrar con tu número de empleado y actualizarlo.' : ''}</p>
                        <button type="button" className="rv-btn rv-btn-ghost" onClick={reset}>Registrar a otra persona</button>
                    </div>
                )}

                <p className="rv-foot">Caprepa · Los datos se usan únicamente para fines laborales internos.</p>
            </main>
        </div>
    );
}
